// Datenstand und Speichervorgang der oeffentlichen Self-Service-Flaeche —
// alles ausser JSX. Herausgeloest aus `SelfEditPage.tsx` (2026-09-15), als der
// feldgenaue Abgleich dazukam und die Seite ueber 550 Zeilen wuchs; dieselbe
// Trennung wie im Admin-Bereich (`useVoiceEditor`), wo Formularzustand und
// Darstellung ebenfalls getrennt liegen.
//
// Bewusst KEIN gemeinsamer Hook mit dem Admin-Bereich: die Self-Service-Flaeche
// liegt ausserhalb von Auth, hat ihr eigenes Bundle, eine andere (viel kleinere)
// Feld-Allowlist und einen eigenen API-Client — geteilt wird nur reine Logik
// (`threeWayMerge`, `saveStatusText`). Alle Texte kommen aus dem Self-Wörterbuch
// (`useT`), Toasts erscheinen in der Sprache zum Zeitpunkt der Meldung.
import { useMemo, useRef, useState } from 'react';
import type { SelfVoiceData, SelfSavePayload, AudioFailure } from '@shared/types';
import type { AudioUploadMeta } from '@/lib/api';
import { useToast } from '@/components/Toast';
import { predictAudioMeta } from '@/lib/predictAudioMeta';
import { classifyAudioFiles } from '@/lib/classifyAudioFiles';
import { toNormalizedMp3 } from '@/lib/audioConvert';
import { threeWayMerge } from '@/lib/threeWayMerge';
import { MAX_UPLOAD_BYTES, UPLOAD_META_DEFAULTS } from '@/pages/admin/audioOptions';
import { selfLabelsFor, type FormState } from './selfFieldLabels';
import type { SelfConflict, SelfConflictChoice } from './SelfConflictModal';
import { selfApi } from './selfApi';
import { useT } from './i18n/LanguageProvider';

export interface StagedFile { id: string; file: File; meta: AudioUploadMeta; }

/** Optionen, die `saveWith()` fuer GENAU DIESEN Aufruf die State-Werte ersetzen lassen. */
interface SaveOptions {
  /** Formular, das gespeichert werden soll (statt `form` aus dem State). */
  formOverride?: FormState;
  /** Fingerabdruck, gegen den optimistisch gesperrt wird (statt `data.version`). */
  expectedVersion?: string | null;
}

export function formFromData(d: SelfVoiceData): FormState {
  return {
    last_name: d.last_name ?? '',
    first_name: d.first_name ?? '',
    phone: d.phone ?? '',
    phone_landline: d.phone_landline ?? '',
    email: d.email ?? '',
    web: d.web ?? '',
    strasse: d.strasse ?? '',
    plz: d.plz ?? '',
    ort: d.ort ?? '',
    land: d.land ?? '',
    eigenes_studio: d.eigenes_studio,
    languages: d.languages,
    dialects: d.dialects,
    accents: d.accents,
  };
}

function payloadFrom(f: FormState, stamp: string | null | undefined, force: boolean): SelfSavePayload {
  return {
    last_name: f.last_name,
    first_name: f.first_name || null,
    phone: f.phone || null,
    phone_landline: f.phone_landline || null,
    email: f.email || null,
    web: f.web || null,
    strasse: f.strasse || null,
    plz: f.plz || null,
    ort: f.ort || null,
    land: f.land || null,
    eigenes_studio: f.eigenes_studio,
    languages: f.languages,
    dialects: f.dialects,
    accents: f.accents,
    expected_version: stamp ?? undefined,
    force: force || undefined,
  };
}

/** Weicht der Name im Formular vom gespeicherten Stand ab? (→ Proben werden umbenannt) */
function nameAbweichend(f: FormState, d: SelfVoiceData): boolean {
  return f.last_name.trim() !== (d.last_name ?? '') || f.first_name.trim() !== (d.first_name ?? '');
}

// Kopiert EIN Feld von der fremden Seite ins Formular. Als generische Funktion,
// weil `ziel[key] = quelle[key]` nur so ohne Cast typpruefbar ist: ueber die
// Vereinigung aller Feldtypen kann der Compiler sonst nicht beweisen, dass
// Quell- und Zieltyp derselbe sind — hier haelt `K` beide Seiten zusammen.
function uebernimmFeld<K extends keyof FormState>(ziel: FormState, quelle: FormState, key: K): void {
  ziel[key] = quelle[key];
}

export interface SelfSave {
  data: SelfVoiceData;
  form: FormState;
  staging: StagedFile[];
  saving: boolean;
  byteProgress: { loaded: number; total: number } | null;
  phaseText: string | null;
  /** Feld-Edit ODER Staging-Datei vorhanden → Sticky-Footer sichtbar. */
  dirty: boolean;
  /** Name im Formular weicht vom gespeicherten ab → Dateinamen-Vorschau an den Proben. */
  nameDirty: boolean;
  conflict: SelfConflict | null;
  failures: AudioFailure[];
  set: <K extends keyof FormState>(key: K, val: FormState[K]) => void;
  addFiles: (files: File[]) => void;
  updateMeta: (id: string, patch: Partial<AudioUploadMeta>) => void;
  removeStaged: (id: string) => void;
  save: () => Promise<void>;
  resolveConflict: (choice: SelfConflictChoice) => void;
  /**
   * Automatisches Übernehmen nach einer Ping-Meldung (Spec §5, seit §7 ohne
   * Banner): laedt den frischen Stand und fuehrt ihn mit demselben Abgleich wie
   * der 409-Pfad ins Formular zusammen — OHNE zu speichern. Eigene Eingaben
   * bleiben; echte Feldkonflikte landen im gewohnten Dialog, dessen Antworten
   * hier ebenfalls nichts schreiben. Hinweis oben per `toast.top`.
   */
  adoptFresh: () => Promise<void>;
  /** true, solange ein Übernehmen laeuft (die Seite startet dann kein zweites). */
  adopting: boolean;
}

export function useSelfSave(token: string, initial: SelfVoiceData): SelfSave {
  const toast = useToast();
  const { lang, t } = useT();
  const [data, setData] = useState<SelfVoiceData>(initial);
  const [form, setForm] = useState<FormState>(() => formFromData(initial));
  const [staging, setStaging] = useState<StagedFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [byteProgress, setByteProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [phaseText, setPhaseText] = useState<string | null>(null);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const [conflict, setConflict] = useState<SelfConflict | null>(null);
  const [failures, setFailures] = useState<AudioFailure[]>([]);
  const [adopting, setAdopting] = useState(false);
  // Re-Entry-Riegel. `saving` allein reicht nicht: der State-Wert ist erst im
  // naechsten Render zu sehen, ein zweiter Aufruf im selben Task saehe noch
  // `false`. Der Riegel bleibt waehrend eines Abgleichs bewusst gehalten —
  // Abgleich und erneutes Speichern sind EIN Vorgang (wie im Admin-Editor).
  const inFlight = useRef(false);
  // Neuester Formularstand, SYNCHRON gepflegt (Review dc16f8d). Das Übernehmen
  // laeuft automatisch, also auch mitten im Tippen: der Abgleich muss nach dem
  // Nachladen gegen das Formular von JETZT laufen, nicht gegen das beim Start
  // eingefangene — sonst ueberschriebe `setForm(merged)` alles, was waehrend des
  // Round-Trips getippt wurde. Jede Formularaenderung laeuft ueber `formSetzen`.
  const formRef = useRef<FormState>(form);
  function formSetzen(next: FormState) {
    formRef.current = next;
    setForm(next);
  }
  // Fehler-Toast beim automatischen Übernehmen nur einmal je Fehlerserie —
  // sonst kaeme er bei dauerhaft scheiterndem Nachladen alle 5 s.
  const adoptFehlerGemeldet = useRef(false);

  const predictVocab = useMemo(
    () => ({ dialects: data.vocab.dialects, accents: data.vocab.accents, categories: data.vocab.categories }),
    [data.vocab],
  );

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    formSetzen({ ...formRef.current, [key]: val });
    setFieldsDirty(true);
  }

  function addFiles(files: File[]) {
    const { accepted, skipped } = classifyAudioFiles(files, MAX_UPLOAD_BYTES);
    if (accepted.length) {
      setStaging((s) => [
        ...s,
        ...accepted.map((file) => ({
          id: crypto.randomUUID(),
          file,
          meta: { ...UPLOAD_META_DEFAULTS, ...predictAudioMeta(file.name, predictVocab) } as AudioUploadMeta,
        })),
      ]);
    }
    if (skipped.length) {
      const nonAudio = skipped.filter((s) => s.reason === 'not_audio').length;
      const tooLarge = skipped.filter((s) => s.reason === 'too_large').length;
      const parts: string[] = [];
      if (nonAudio) parts.push(t.skippedNotAudio(nonAudio));
      if (tooLarge) parts.push(t.skippedTooLarge(tooLarge));
      toast.error(t.skipped(parts.join(', ')));
    }
  }

  function updateMeta(id: string, patch: Partial<AudioUploadMeta>) {
    setStaging((s) => s.map((st) => (st.id === id ? { ...st, meta: { ...st.meta, ...patch } } : st)));
  }
  function removeStaged(id: string) {
    setStaging((s) => s.filter((st) => st.id !== id));
  }

  /**
   * Aufloesung eines 409 per Drei-Wege-Abgleich (Spec 2026-09-15 §4). Laedt den
   * frischen Serverstand nach und fuehrt ihn feldgenau mit dem Formular
   * zusammen: `basis` ist der Stand, auf dem meine Eingaben sitzen, „mine" das
   * Formular zum Zeitpunkt NACH dem Nachladen (`formRef`, damit waehrend des
   * Round-Trips Getipptes mitkommt), der frische Stand ist „theirs".
   *
   * Rueckgabe: der zusammengefuehrte Stand fuer einen SOFORTIGEN zweiten
   * Speicherversuch — oder `null`, wenn stattdessen der Dialog entscheidet
   * (echte Feldkonflikte, oder der frische Stand war nicht ladbar). `basis` kommt
   * als Argument statt aus dem State: die Funktion laeuft innerhalb desselben
   * `saveWith()`-Aufrufs, dessen Closure die zwischenzeitlichen State-Updates
   * nicht sieht. `data` aendert sich waehrend des Nachladens nicht (Riegel
   * `inFlight`), Eingaben in dieser Zeit sitzen also auf derselben Basis.
   */
  async function resolveByMerge(
    basis: SelfVoiceData,
    modus: 'save' | 'adopt' = 'save',
  ): Promise<{ form: FormState; data: SelfVoiceData; stamp: string | null } | null> {
    let fresh: SelfVoiceData;
    try {
      fresh = await selfApi.getData(token);
    } catch (e) {
      // Ohne den frischen Stand ist kein Abgleich moeglich. Beim Speichern bleibt
      // der Dialog als Notausgang — ohne Feldliste, aber mit „Meine behalten".
      // Beim automatischen Übernehmen (adopt) gibt es nichts zu entscheiden: es
      // stand kein Speichern an. Fehler-Toast; die Seite verwirft die Meldung,
      // der naechste Ping versucht es erneut.
      console.error('[self merge] aktuellen Stand laden fehlgeschlagen:', e);
      if (modus === 'save') {
        setConflict({ fields: [], theirs: null, reason: 'reload_failed' });
      } else if (!adoptFehlerGemeldet.current) {
        adoptFehlerGemeldet.current = true;
        toast.error(t.adoptLoadFailed);
      }
      return null;
    }
    if (modus === 'adopt') adoptFehlerGemeldet.current = false; // Serie beendet
    const theirs = formFromData(fresh);
    const { merged, conflicts, theirsApplied } = threeWayMerge(formFromData(basis), formRef.current, theirs);
    // Ab jetzt ist der frische Stand der Bezugspunkt: gesperrte Dialekte/Akzente,
    // Proben, Fingerabdruck.
    setData(fresh);
    formSetzen(merged);
    // Uebernommene Fremdfelder werden IMMER gemeldet — auch wenn daneben ein
    // Konflikt im Dialog landet. Der Dialog spricht nur von den strittigen
    // Feldern; dass still auch andere Werte gewechselt haben, waere sonst
    // nirgends zu sehen.
    if (modus === 'adopt') {
      // Automatisches Übernehmen (Spec §7): kurzer Hinweis oben. Ohne
      // uebernommene Felder (nur Proben oder gesperrte Listen haben sich
      // geaendert) ohne Feldliste — aber nur, wenn nicht ohnehin der Dialog kommt.
      if (theirsApplied.length > 0) toast.top(t.adoptedFields(selfLabelsFor(theirsApplied, lang)));
      else if (conflicts.length === 0) toast.top(t.adopted);
    } else if (theirsApplied.length > 0) {
      toast.success(t.merged(selfLabelsFor(theirsApplied, lang)));
    }
    if (conflicts.length > 0) {
      setConflict({ fields: conflicts, theirs, mode: modus });
      return null;
    }
    return { form: merged, data: fresh, stamp: fresh.version };
  }

  async function saveWith(force = false, opts: SaveOptions = {}) {
    if (saving || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setFailures([]);
    const startForm = opts.formOverride ?? form;
    // Namensänderung → der Server benennt alle Proben synchron auf der Cloud um.
    setPhaseText(t.saveStatus(nameAbweichend(startForm, data) ? data.audios.length : 0));
    try {
      // Laufender Stand DIESES Speichervorgangs. Bewusst lokale Variablen statt
      // State-Reads: nach einem Abgleich haben setForm/setData den React-State
      // zwar aktualisiert, die Closure dieses Aufrufs sieht davon nichts.
      let aktuellesForm = startForm;
      let aktuelleDaten = data;
      let stempel = opts.expectedVersion !== undefined ? opts.expectedVersion : data.version;
      // Zaehlt die 409 DIESES Vorgangs: nach dem ersten und dem zweiten wird
      // abgeglichen und erneut gespeichert, beim dritten entscheidet der Nutzer.
      let konflikte = 0;
      let res: { ok: true; audio_failures: AudioFailure[] };
      for (;;) {
        try {
          res = await selfApi.save(token, payloadFrom(aktuellesForm, stempel, force));
          break;
        } catch (e) {
          const err = e as { status?: number };
          if (err.status !== 409) throw e; // echte Fehler: unveraendert unten melden
          konflikte += 1;
          if (konflikte > 2) {
            // Dritter 409 in Folge: ein weiterer Abgleich wuerde nur dasselbe
            // Rennen verlieren. Jetzt entscheidet der Nutzer — ohne Feldliste,
            // weil dieser Stand nicht mehr einem einzelnen Abgleich entspricht.
            setConflict({ fields: [], theirs: null, reason: 'repeated' });
            return;
          }
          const abgleich = await resolveByMerge(aktuelleDaten);
          if (!abgleich) return; // echte Feldkonflikte → der Dialog entscheidet
          aktuellesForm = abgleich.form;
          aktuelleDaten = abgleich.data;
          stempel = abgleich.stamp;
        }
      }
      // Sprachproben: erst client-seitig zu MP3 192k / -14 LUFS normalisieren, dann
      // hochladen. Sie laufen bewusst NACH der erfolgreichen Stammdaten-Antwort —
      // ein Abgleich zwischendurch wiederholt nur den PUT, nie die Uploads.
      // Auto-Naming/-Belegung passiert serverseitig.
      if (staging.length) {
        const total = staging.length;
        // Phase 1: konvertieren
        const ready: { st: StagedFile; blob: Blob }[] = [];
        setByteProgress({ loaded: 0, total });
        setPhaseText(t.convertProgress(0, total));
        for (const st of staging) {
          const blob = await toNormalizedMp3(st.file);
          ready.push({ st, blob });
          setByteProgress({ loaded: ready.length, total });
          setPhaseText(t.convertProgress(ready.length, total));
        }
        // Phase 2: hochladen (echter Byte-Progress der kleinen MP3s)
        const totalBytes = ready.reduce((s, r) => s + r.blob.size, 0);
        let sentBytes = 0;
        let up = 0;
        setByteProgress({ loaded: 0, total: totalBytes });
        for (const { st, blob } of ready) {
          setPhaseText(t.uploadProgress(up, total));
          await selfApi.uploadAudio(token, blob, {
            category: st.meta.category,
            language: st.meta.language,
            dialect: st.meta.dialect,
            accent: st.meta.accent,
            is_demomix: st.meta.is_demomix,
            freetext_suffix: st.meta.freetext_suffix,
          }, st.file.name, ({ loaded }) => {
            setByteProgress({ loaded: sentBytes + Math.min(loaded, blob.size), total: totalBytes });
          });
          sentBytes += blob.size;
          up++;
          setByteProgress({ loaded: sentBytes, total: totalBytes });
        }
      }
      // Frisch laden → aktualisierte locked_*/audios; Staging leeren.
      const fresh = await selfApi.getData(token);
      setData(fresh);
      formSetzen(formFromData(fresh));
      setStaging([]);
      setFieldsDirty(false);
      setConflict(null);
      const fails = res.audio_failures ?? [];
      setFailures(fails);
      if (fails.length > 0) {
        toast.error(t.savedWithRenameFailures(fails.length));
      } else {
        toast.success(t.saved);
      }
    } catch (e) {
      // 409 ist hier nicht mehr zu erwarten — die Schleife oben faengt jeden
      // Konflikt ab und entscheidet ihn (Abgleich oder Dialog).
      console.error('[self save/convert] fehlgeschlagen:', e);
      toast.error(t.saveFailed);
    } finally {
      inFlight.current = false;
      setSaving(false);
      setByteProgress(null);
      setPhaseText(null);
    }
  }

  /**
   * Antwort auf den Konflikt-Dialog. Der frische Stand ist zu diesem Zeitpunkt
   * schon geladen (der Abgleich hat ihn geholt) — deshalb gibt es kein „Neu
   * laden" mehr, und `data.version` traegt bereits den neuen Fingerabdruck, mit dem
   * „Fremde übernehmen" regulaer (ohne force) speichern kann.
   */
  function resolveConflict(choice: SelfConflictChoice) {
    const offen = conflict;
    setConflict(null);
    // „OK" des Dialogs ohne Feldliste: schliessen, Formular bleibt wie es ist.
    if (choice === 'dismiss') return;
    // Im adopt-Modus (automatisches Übernehmen) schreibt KEINE der Antworten: der
    // Sprecher hat nur nachgezogen, nicht gespeichert. „Meine behalten" laesst
    // das Formular unveraendert (es traegt in den strittigen Feldern bereits
    // meine Werte), „Fremde übernehmen" setzt genau diese Felder um.
    const nurFormular = offen?.mode === 'adopt';
    if (choice === 'keepMine') {
      // Ohne Rueckmeldung sieht „Meine behalten" hier aus wie ein verschlucktes
      // Speichern: der Dialog geht zu, das Formular sieht aus wie vorher.
      if (nurFormular) toast.success(t.keptMine);
      else void saveWith(true);
      return;
    }
    if (!offen || !offen.theirs) return;
    const uebernommen: FormState = { ...formRef.current };
    for (const key of offen.fields) uebernimmFeld(uebernommen, offen.theirs, key);
    formSetzen(uebernommen);
    if (nurFormular) return;
    // `formOverride`/`expectedVersion`, weil `form` aus der Closure in diesem
    // Task noch den Stand VOR dem Setzen zeigt.
    void saveWith(false, { formOverride: uebernommen, expectedVersion: data.version });
  }

  /**
   * Automatisches Übernehmen nach einer Ping-Meldung (Spec §5/§7). Derselbe Abgleich wie im
   * 409-Pfad, nur ohne anschliessendes Speichern: danach ist `data` der frische
   * Stand, das naechste Speichern laeuft also ohne 409 durch.
   */
  async function adoptFresh() {
    if (saving || inFlight.current) return;
    inFlight.current = true;
    setAdopting(true);
    try {
      await resolveByMerge(data, 'adopt');
    } finally {
      setAdopting(false);
      inFlight.current = false;
    }
  }

  return {
    data, form, staging, saving, byteProgress, phaseText,
    dirty: fieldsDirty || staging.length > 0,
    nameDirty: nameAbweichend(form, data),
    conflict, failures,
    set, addFiles, updateMeta, removeStaged,
    save: () => saveWith(false),
    resolveConflict,
    adoptFresh,
    adopting,
  };
}
